import type { RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context/requestContext';
import { getConfluenceOnboardingUrl as getConfluenceOnboardingUrlHelper } from '@post-office/message-templates-common--growth-starburst/utils/get-confluence-onboarding-url';
import { getProductsFromTcs } from '@post-office/message-templates-common--growth-starburst/utils/get-products-from-tcs';
import { getConfSamplePageLocation } from '@post-office/message-templates-common--growth-starburst/utils/get-conf-sample-page-location';
import { type BackendMessageBody } from '@post-office/placement-common-v2/types';
import { type SerializableRecord } from '@post-office/serializable-record';

/**
 * Fetches TCS hostname and TAP site trait to build the Confluence onboarding URL.
 * @param requestContext - The request context containing TCS client, TAP provider, and orchestration context
 * @param message - The message body containing message template ID, message instance ID, and trigger ID
 * @returns The Confluence onboarding URL, or undefined if not found
 */
export const getConfluenceOnboardingUrl = async (
	requestContext: RequestContext,
	message: BackendMessageBody<SerializableRecord>,
): Promise<string | undefined> => {
	const { logger, orchestrationContext, restApiClients, tapProvider } = requestContext;
	const { tenantId } = orchestrationContext;
	const { messageTemplateId, messageInstanceId, triggerId } = message;

	if (!tenantId) {
		logger.error(
			{ messageTemplateId, messageInstanceId, triggerId },
			'Tenant ID not available, cannot fetch Confluence onboarding URL',
		);
		return undefined;
	}

	if (!tapProvider) {
		logger.error(
			{ messageTemplateId, messageInstanceId, triggerId },
			'TAP provider not available, cannot fetch Confluence onboarding URL',
		);
		return undefined;
	}

	try {
		// Fetch hostname from TCS and site trait from TAP in parallel
		const [{ hostname }, confSamplePageLocation] = await Promise.all([
			getProductsFromTcs({
				messageTemplateId,
				siteId: tenantId,
				tcsClient: restApiClients.tcsClient,
				logger,
			}),
			getConfSamplePageLocation({
				tenantId,
				tapProvider,
				logger,
			}),
		]);

		const onboardingUrl = getConfluenceOnboardingUrlHelper(hostname, confSamplePageLocation);

		if (!onboardingUrl) {
			logger.error(
				{ messageTemplateId, messageInstanceId, triggerId, hostname, confSamplePageLocation },
				'Could not build Confluence onboarding URL from hostname and trait',
			);
		}

		return onboardingUrl;
	} catch (error) {
		logger.error(
			{ messageTemplateId, messageInstanceId, triggerId, error, tenantId },
			'Error building Confluence onboarding URL',
		);
		return undefined;
	}
};
